import { mapState } from 'vuex';
import isEmpty from 'lodash/isEmpty';
import { cloneDeep } from 'lodash';
import get from 'lodash/get';
export default {
  name: 'CustomMultiSearch',
  props: {
    dense: {
      type: Boolean,
      default: false,
      required: false
    },
    searchTypes: {
      type: Array,
      required: true
    },
    searchItems: {
      type: Array,
      required: false,
      default: () => []
    },
    singleSearch: {
      type: Boolean,
      required: false,
      default: false
    },
    defaultSearchType: {
      type: Object,
      required: false,
      default: () => ({})
    },
    showOperatorDropdown: {
      type: Boolean,
      required: false,
      default: false
    },
    operator: {
      type: String,
      required: false,
      default: 'AND'
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data: function () {
    return {
      modelSearchType: '',
      modelSearchFor: '',
      modelSearchKey: '',
      searchTags: [],
      searchOperator: this.operator,
      customSearchInput: '',
      customSearchResult: [],
      customSearchResultLoading: false,
      customSearchSelectedItemCopy: null,
      customSearchPageSize: 50,
      customSearchHasMore: true,
      isIntersecting: false
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme', 'permissions']),
    disableAddSearch() {
      return !(this.modelSearchType !== '' && this.modelSearchFor !== '');
    },
    cols() {
      const hasKey = get(this.modelSearchType, 'param') === 'keyValue' || get(this.modelSearchType, 'param') === 'ddrTarget' || get(this.modelSearchType, 'param') === 'ddrDestination';
      return {
        category: {
          cols: hasKey ? 4 : 6,
          md: hasKey ? 4 : 2,
          lg: 3
        },
        keyBox: {
          cols: hasKey ? 3 : 0,
          md: hasKey ? 4 : 0,
          lg: hasKey ? 2 : 0
        },
        searchBox: {
          cols: hasKey ? 5 : 6,
          md: hasKey ? 4 : 6,
          lg: hasKey ? 3 : 4
        },
        displayBox: {
          cols: 12,
          md: hasKey ? 12 : 4,
          lg: hasKey ? 4 : 5
        }
      };
    },
    searchOperators() {
      return [{
        text: this.$i18n_t('Common.searchOperator.AND'),
        param: 'AND'
      }, {
        text: this.$i18n_t('Common.searchOperator.OR'),
        param: 'OR'
      }];
    },
    useCustomSearch() {
      if (!this.modelSearchType) {
        return false;
      }
      const searchHandlerType = typeof get(this.modelSearchType, 'searchHandler');
      return searchHandlerType === 'function';
    },
    customSearchResultWithSelected() {
      let items = [];
      if (this.customSearchResult.pages && this.customSearchResult.pages.length) {
        items = cloneDeep(this.customSearchResult.pages.flat());
      }
      if (this.customSearchSelectedItemCopy) {
        items.push(this.customSearchSelectedItemCopy);
      }

      // unique by id
      return items.map((item, index) => ({
        ...item,
        __doIntersect__: index >= items.length - this.customSearchPageSize / 2
      }));
    },
    itemsForSearchKey() {
      if (this.modelSearchKey && !this.customSearchResultWithSelected.find(item => item.value === this.modelSearchKey)) {
        return [this.modelSearchKey, ...this.customSearchResultWithSelected];
      }
      return this.customSearchResultWithSelected;
    },
    itemsForSearchFor() {
      if (this.modelSearchFor && !this.customSearchResultWithSelected.find(item => item.value === this.modelSearchFor)) {
        return [this.modelSearchFor, ...this.customSearchResultWithSelected];
      }
      return this.customSearchResultWithSelected;
    }
  },
  watch: {
    searchTypes() {
      const store = get(this.modelSearchType, 'param');
      this.modelSearchType = this.searchTypes.find(item => item.param === store) || {};
    },
    searchItems() {
      this.searchTags = this.searchItems;
    },
    operator() {
      this.searchOperator = this.searchOperators.filter(item => item.param === this.operator)[0] || {};
    },
    customSearchInput: {
      immediate: true,
      handler() {
        this.resetCustomSearch();
        this.executeCustomSearch();
      }
    },
    modelSearchFor(value) {
      if (this.useCustomSearch) {
        this.customSearchSelectedItemCopy = cloneDeep(value);
      }
    },
    modelSearchType() {
      this.resetCustomSearch();
      this.executeCustomSearch();
    },
    isIntersecting() {
      if (!this.customSearchResultLoading) {
        this.executeCustomSearch();
      }
    },
    customSearchResultLoading() {
      if (this.isIntersecting) {
        this.executeCustomSearch();
      }
    }
  },
  mounted() {
    this.searchTags = this.searchItems;
    this.modelSearchType = isEmpty(this.defaultSearchType) ? '' : this.defaultSearchType;
    if (this.searchOperators.length > 0) {
      this.searchOperator = this.searchOperators.filter(item => item.param === this.operator)[0] || {};
    }
  },
  methods: {
    get,
    resetCustomSearch() {
      this.customSearchResult = {
        total: 0,
        hasMore: true,
        pages: []
      };
    },
    async executeCustomSearch(entityType, key) {
      if (!this.useCustomSearch) {
        return;
      }
      if (this.customSearchResultLoading) {
        return;
      }
      if (!this.customSearchResult.hasMore) {
        return;
      }
      this.customSearchResultLoading = true;
      try {
        const previouslyFetchedPages = get(this.customSearchResult, 'pages', []);
        const pageToFetch = previouslyFetchedPages.length;
        const fetchedItems = await this.modelSearchType.searchHandler(this.customSearchInput, pageToFetch, this.customSearchPageSize, entityType, key);
        const allPreviouslyFetchedItems = previouslyFetchedPages.flat();
        const allFetchedItems = [...allPreviouslyFetchedItems, ...fetchedItems.items];
        const hasMore = fetchedItems.totalCount > allFetchedItems.length;
        this.customSearchResult = {
          total: fetchedItems.totalCount,
          hasMore,
          pages: [...previouslyFetchedPages, fetchedItems.items]
        };
        this.isIntersecting = false;
      } catch (error) {
        console.error(error);
        this.$snack(this.$i18n_t('Common.multisearch.customSearch.error'), 'error');
      } finally {
        this.customSearchResultLoading = false;
      }
    },
    updateSearchType() {
      this.modelSearchFor = '';
      this.modelSearchKey = '';
    },
    searchKeyUpEnter() {
      if (!this.disableAddSearch) {
        this.searchAdd();
      }
    },
    parentTwinSelected(parentTwin) {
      this.modelSearchFor = parentTwin ? {
        value: parentTwin.id,
        text: parentTwin.title
      } : undefined;
      this.searchAdd();
    },
    searchRemove(searchItemIndex) {
      this.searchTags.splice(searchItemIndex, 1);
      this.updateSearchType();
      this.$emit('updated', this.searchTags, this.searchOperator.param);
    },
    searchOperatorUpdate() {
      this.searchTags.splice(0, this.searchTags.length);
      this.updateSearchType();
      this.$emit('updated', this.searchTags, this.searchOperator.param);
    },
    searchAdd() {
      const type = get(this.modelSearchType, 'text');
      const param = get(this.modelSearchType, 'param');
      // make sure that >null< is supported and not written as string "null" via `${text}`.trim()
      let searchForText = (this.modelSearchFor || {}).text !== undefined ? this.modelSearchFor.text : this.modelSearchFor;
      searchForText = searchForText === null ? searchForText : `${searchForText}`.trim();
      const search = (this.modelSearchFor || {}).text ? {
        value: this.modelSearchFor.value,
        text: searchForText
      } : searchForText;

      // If the to be added search already exists in `searchTags` ignore it
      const index = this.searchTags.findIndex(item => item.param === param && item.search === search);
      if (index > -1) {
        this.updateSearchType();
        return;
      }
      let key;
      if (param === 'keyValue' || param === 'ddrTarget' || param === 'ddrDestination') {
        const searchKey = this.modelSearchKey;
        if (searchKey) {
          key = typeof searchKey.text === 'string' ? searchKey.text.trim() : String(searchKey).trim();
        } else {
          key = '';
        }
      }
      this.searchTags.push({
        type,
        param,
        search,
        key
      });
      this.updateSearchType();
      this.$emit('updated', this.searchTags, this.searchOperator.param);
    },
    async endCustomSearchIntersect(entries, observer, isIntersecting) {
      if (!isIntersecting) {
        return;
      }
      this.isIntersecting = true;
    },
    async fetchDdrValues(key) {
      if (get(this.modelSearchType, 'param') === 'ddrTarget' || get(this.modelSearchType, 'param') === 'ddrDestination') {
        this.resetCustomSearch();
        this.executeCustomSearch('values', key.text);
      }
    },
    async fetchDdrKeys() {
      if (get(this.modelSearchType, 'param') === 'ddrTarget' || get(this.modelSearchType, 'param') === 'ddrDestination') {
        this.resetCustomSearch();
        this.executeCustomSearch();
      }
    }
  }
};